/**
 * Shared styles for IELTS passage questions components
 * Used by ListeningTestUI and ListeningTestUI2
 * Standardized for professional IELTS practice interface
 */

// ============================================================================
// CONSTANTS - STANDARDIZED FOR IELTS PRACTICE
// ============================================================================

const COLORS = {
  primary: '#996CFE',
  primaryDark: '#7C4DFF',
  primaryLight: 'rgba(153, 108, 254, 0.2)',
  text: '#333',
  textDark: '#222',
  border: '#bbb',
  borderLight: '#ccc',
  backgroundGray: '#f9f9f9',
  backgroundLightGray: '#e9e9ef',
  white: '#ffffff',
};

const SPACING = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  xxl: '24px',
};

const SIZES = {
  fillBlankInputWidth: '100px',
  dropdownMinWidth: '140px',
  questionNumberWidth: '32px',
  borderRadius: '4px',
  borderRadiusLg: '8px',
  borderWidth: '1px',
  focusRingWidth: '2px',
};

const TYPOGRAPHY = {
  // Font sizes - standardized scale
  baseFontSize: '16px',
  smallFontSize: '15px',
  h1Size: '24px',        // 1.5rem
  h2Size: '20px',        // 1.25rem
  h3Size: '18px',        // 1.125rem
  
  // Line heights - consistent vertical rhythm
  baseLineHeight: '1.6',
  headingLineHeight: '1.4',
  tightLineHeight: '1.5',
  
  // Font weights
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  
  // Spacing scale
  headingMarginBottom: {
    h1: '16px',
    h2: '12px',
    h3: '10px',
    p: '14px',
  },
};

const LAYOUT = {
  tableMargin: `${SPACING.xl} 0`,
  tableCellPadding: SPACING.md,
  tableCellPaddingLarge: `${SPACING.md} ${SPACING.lg}`,
  inputPadding: `6px ${SPACING.sm}`,
  dropdownPadding: `${SPACING.sm} ${SPACING.md}`,
  // Simple table (non-TFN) - match admin editor exactly
  simpleTableRowPadding: '3px 5px',
  simpleTableRowLineHeight: '1.55',
  simpleTableParagraphMargin: '0 0 6px',
  simpleTableRowFontSize: TYPOGRAPHY.smallFontSize,
  simpleTableRowMinHeight: '26px',
  // Simple table header - same padding as rows, no extra height or bg
  simpleTableHeaderPadding: '3px 5px',
  simpleTableHeaderLineHeight: '1',
  simpleTableHeaderFontSize: '14px',
  simpleTableBorderColor: '#ddd',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const createFocusStyles = () => ({
  outline: 'none',
  borderColor: COLORS.primaryDark,
  boxShadow: `0 0 0 ${SIZES.focusRingWidth} ${COLORS.primaryLight}`,
});

const createInputStyles = () => ({
  border: `${SIZES.borderWidth} solid ${COLORS.primary}`,
  borderRadius: SIZES.borderRadius,
  fontSize: TYPOGRAPHY.baseFontSize,
  color: COLORS.text,
  fontFamily: 'inherit',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  '&:focus': createFocusStyles(),
});

const createTableCellStyles = () => ({
  padding: LAYOUT.simpleTableRowPadding,
  height: LAYOUT.simpleTableRowMinHeight,
  border: `${SIZES.borderWidth} solid ${LAYOUT.simpleTableBorderColor}`,
  textAlign: 'left',
  verticalAlign: 'top',
  fontSize: LAYOUT.simpleTableRowFontSize,
  lineHeight: LAYOUT.simpleTableRowLineHeight,
  '& p': {
    margin: LAYOUT.simpleTableParagraphMargin,
    lineHeight: LAYOUT.simpleTableRowLineHeight,
  },
  '& p:last-child': {
    marginBottom: 0,
  },
});

const createTfnTableCellStyles = () => ({
  padding: LAYOUT.tableCellPaddingLarge,
  border: `${SIZES.borderWidth} solid ${COLORS.borderLight}`,
  textAlign: 'left',
  lineHeight: TYPOGRAPHY.tightLineHeight,
  fontSize: TYPOGRAPHY.baseFontSize,
});

const createHeadingStyles = (fontSize, marginBottom, lineHeight = TYPOGRAPHY.headingLineHeight) => ({
  fontSize,
  marginBottom,
  lineHeight,
  fontWeight: TYPOGRAPHY.semibold,
  color: COLORS.text,
});

// ============================================================================
// STYLE OBJECTS
// ============================================================================

const inputStyles = {
  '& .fill-blank-input': {
    width: SIZES.fillBlankInputWidth,
    margin: `0 ${SPACING.xs}`,
    padding: LAYOUT.inputPadding,
    ...createInputStyles(),
  },

  '& .dropdown-selection-field': {
    ...createInputStyles(),
    padding: LAYOUT.dropdownPadding,
    marginRight: SPACING.xs,
    backgroundColor: COLORS.white,
    minWidth: SIZES.dropdownMinWidth,
    cursor: 'pointer',
    '&:hover': {
      borderColor: COLORS.primaryDark,
    },
  },

  '& .selection-qnum': {
    fontWeight: TYPOGRAPHY.bold,
    color: COLORS.primary,
    marginRight: SPACING.sm,
    fontSize: TYPOGRAPHY.baseFontSize,
  },
};

const baseTableStyles = {
  '& .tiptap-table': {
    width: '100%',
    maxWidth: '100%',
    borderCollapse: 'collapse',
    // Auto layout allows columns to shrink proportionally based on content
    tableLayout: 'auto',
    margin: LAYOUT.tableMargin,
  },

  '& .tiptap-table:not([data-table-type="tfn"]) th': {
    padding: LAYOUT.simpleTableHeaderPadding,
    border: `${SIZES.borderWidth} solid ${LAYOUT.simpleTableBorderColor}`,
    textAlign: 'center',
    verticalAlign: 'middle',
    fontSize: LAYOUT.simpleTableHeaderFontSize,
    fontWeight: TYPOGRAPHY.bold,
    lineHeight: LAYOUT.simpleTableHeaderLineHeight,
  },

  '& .tiptap-table:not([data-table-type="tfn"]) td': createTableCellStyles(),
};

const tfnTableStyles = {
  '& table.tiptap-table[data-table-type="tfn"]': {
    width: '100%',
    maxWidth: '100%',
    borderCollapse: 'collapse',
    margin: LAYOUT.tableMargin,
    borderRadius: SIZES.borderRadiusLg,
    overflow: 'hidden',
    tableLayout: 'auto',
  },

  '& table.tiptap-table[data-table-type="tfn"] td': {
    ...createTfnTableCellStyles(),
  },

  '& table.tiptap-table[data-table-type="tfn"] td p': {
    margin: 0,
    fontSize: TYPOGRAPHY.baseFontSize,
    lineHeight: TYPOGRAPHY.tightLineHeight,
  },

  // Remove vertical dividers between columns
  '& table.tiptap-table[data-table-type="tfn"] td:first-child': {
    borderRight: 'none',
  },

  '& table.tiptap-table[data-table-type="tfn"] td:last-child': {
    borderLeft: 'none',
  },

  // Row background colors
  '& table.tiptap-table[data-table-type="tfn"] tr:first-child td': {
    backgroundColor: COLORS.backgroundLightGray,
    fontWeight: TYPOGRAPHY.medium,
  },

  '& table.tiptap-table[data-table-type="tfn"] tr:nth-child(2) td': {
    backgroundColor: COLORS.white,
  },

  '& table.tiptap-table[data-table-type="tfn"] tr:last-child td': {
    backgroundColor: COLORS.backgroundLightGray,
    fontWeight: TYPOGRAPHY.medium,
  },
};

const hiddenElementStyles = {
  '& .tiptap-table .answer-wrapper': {
    display: 'none !important',
  },

  '& .correct-answer-indicator': {
    display: 'none !important',
  },

  '& .question-box-header .question-number': {
    display: 'none !important',
  },

  '& .question-number': {
    display: 'none !important',
  },

  '& .q-marker': {
    display: 'none !important',
  },

  '& .question-number-input': {
    display: 'none !important',
  },
};

const questionBoxStyles = {
  '& .question-box-header': {
    display: 'none !important',
  },

  '& .question-box': {
    border: 'none !important',
    borderRadius: '0 !important',
    marginBottom: '0 !important',
    boxShadow: 'none !important',
    backgroundColor: 'transparent !important',
    padding: '0 !important',
    width: '100%',
  },

  '& .question-box-content': {
    padding: '0 !important',
    backgroundColor: 'transparent !important',
  },
};

const typographyStyles = {
  '& h1': createHeadingStyles(
    TYPOGRAPHY.h1Size,
    TYPOGRAPHY.headingMarginBottom.h1
  ),
  
  '& h2': createHeadingStyles(
    TYPOGRAPHY.h2Size,
    TYPOGRAPHY.headingMarginBottom.h2
  ),
  
  '& h3': createHeadingStyles(
    TYPOGRAPHY.h3Size,
    TYPOGRAPHY.headingMarginBottom.h3
  ),
  
  '& p': {
    fontSize: TYPOGRAPHY.baseFontSize,
    marginBottom: TYPOGRAPHY.headingMarginBottom.p,
    color: COLORS.textDark,
    lineHeight: TYPOGRAPHY.baseLineHeight,
  },
};

// ============================================================================
// MAIN EXPORT - IELTS PASSAGE GLOBAL STYLES
// ============================================================================

export const ieltsPassageGlobalStyles = {
  '.ielts-passage-questions': {
    // Prevent any horizontal scrolling inside questions area
    maxWidth: '100%',
    overflowX: 'hidden',
    ...inputStyles,
    ...baseTableStyles,
    ...tfnTableStyles,
    ...hiddenElementStyles,
    ...questionBoxStyles,
    ...typographyStyles,

    // Make all table content wrap instead of overflowing
    '& table, & .tiptap-table': {
      width: '100% !important',
      maxWidth: '100% !important',
      tableLayout: 'auto !important', // Auto preserves admin column ratios
    },
    // CRITICAL: Keep colgroup visible but allow shrinking to preserve admin ratios
    '& table colgroup col': {
      minWidth: '0 !important', // Allow colgroup columns to shrink
    },
    '& table th[width], & table td[width]': {
      minWidth: '0 !important', // Allow cells with width attr to shrink
    },
    '& table th[style*="width"], & table td[style*="width"]': {
      minWidth: '0 !important', // Allow cells with inline width to shrink
    },
    '& table [style*="min-width"]': {
      minWidth: '0 !important',
    },
    '& th, & td': {
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
      whiteSpace: 'normal',
      minWidth: '0 !important', // Allows ALL columns to shrink proportionally
    },
    '& img': {
      maxWidth: '100%',
      height: 'auto',
    },
    // In tables, inputs/selects should shrink to fit cell
    '& table .fill-blank-input': {
      width: '90px',
      minWidth: '0 !important',
      maxWidth: '100%',
      boxSizing: 'border-box',
    },
    '& table .dropdown-selection-field': {
      minWidth: '0 !important',
      maxWidth: '100%',
      boxSizing: 'border-box',
    },
  },
};

// ============================================================================
// READING-SPECIFIC STYLES (for split-pane layout with narrow content area)
// ============================================================================

export const ieltsReadingPassageStyles = {
  // Same as global/admin simple table: fixed layout so column widths from rich text editor are preserved
  '.ielts-passage-questions table:not([data-table-type="tfn"]), .ielts-passage-html table:not([data-table-type="tfn"])': {
    width: '100% !important',
    maxWidth: '100% !important',
    tableLayout: 'fixed !important', // Preserves colgroup/cell widths set in admin
  },
  // Preserve original column widths from admin (colgroup col) – do not override
  // colgroup col keeps its width from HTML (set in admin rich text editor)
  '.ielts-passage-questions table:not([data-table-type="tfn"]) colgroup col, .ielts-passage-html table:not([data-table-type="tfn"]) colgroup col': {
    minWidth: '0 !important', // Allow shrink on narrow viewports only
  },
  // Preserve first-row/cell widths from admin; allow wrapping inside cells
  '.ielts-passage-questions table:not([data-table-type="tfn"]) th, .ielts-passage-questions table:not([data-table-type="tfn"]) td, .ielts-passage-html table:not([data-table-type="tfn"]) th, .ielts-passage-html table:not([data-table-type="tfn"]) td': {
    minWidth: '0 !important',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    boxSizing: 'border-box',
  },
};

// ============================================================================
// QUESTION NUMBER STYLES
// ============================================================================

export const questionNumberStyles = {
  '& .question-number': {
    display: 'block',
    fontWeight: TYPOGRAPHY.bold,
    color: COLORS.primary,
    marginBottom: SPACING.sm,
    fontSize: TYPOGRAPHY.baseFontSize,
    lineHeight: TYPOGRAPHY.headingLineHeight,
  },

  '& .question-number-input': {
    width: SIZES.questionNumberWidth,
    textAlign: 'center',
    color: COLORS.primary,
    fontWeight: TYPOGRAPHY.bold,
    background: 'transparent',
    border: 'none',
    marginRight: SPACING.sm,
    fontSize: TYPOGRAPHY.baseFontSize,
  },
};

// ============================================================================
// INLINE STYLES (for <style> tags)
// ============================================================================

export const questionNumberInlineStyles = `
  .ielts-passage-questions .question-number {
    display: block !important;
    font-weight: ${TYPOGRAPHY.bold};
    color: ${COLORS.primary};
    margin-bottom: ${SPACING.sm};
    font-size: ${TYPOGRAPHY.baseFontSize};
    line-height: ${TYPOGRAPHY.headingLineHeight};
  }
  
  .ielts-passage-questions .question-number-input {
    width: ${SIZES.questionNumberWidth};
    text-align: center;
    color: ${COLORS.primary};
    font-weight: ${TYPOGRAPHY.bold};
    background: transparent;
    border: none;
    margin-right: ${SPACING.sm};
    font-size: ${TYPOGRAPHY.baseFontSize};
  }
`;

export const ieltsPassageInlineStyles = `
  /* ===================================================================== */
  /* BASE PASSAGE STYLES */
  /* ===================================================================== */
  
  .ielts-passage-questions {
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
    font-size: ${TYPOGRAPHY.baseFontSize};
    line-height: ${TYPOGRAPHY.baseLineHeight};
    color: ${COLORS.textDark};
  }
  
  /* ===================================================================== */
  /* TABLE STYLES */
  /* ===================================================================== */
  
  .ielts-passage-questions table {
    width: 100% !important;
    max-width: 100% !important;
    border-collapse: collapse;
    table-layout: auto !important;
    margin: ${LAYOUT.tableMargin};
  }

  .ielts-passage-questions table [style*="min-width"] {
    min-width: 0 !important;
  }

  /* Keep colgroup visible but allow shrinking to preserve admin ratios */
  .ielts-passage-questions table colgroup col {
    min-width: 0 !important;
  }

  .ielts-passage-questions table th[width],
  .ielts-passage-questions table td[width] {
    min-width: 0 !important;
  }

  .ielts-passage-questions table th[style*="width"],
  .ielts-passage-questions table td[style*="width"] {
    min-width: 0 !important;
  }

  .ielts-passage-questions th,
  .ielts-passage-questions td {
    overflow-wrap: anywhere;
    word-break: break-word;
    white-space: normal;
    min-width: 0 !important; /* Allows ALL columns to shrink, not just last ones */
  }

  .ielts-passage-questions table .fill-blank-input {
    width: 90px;
    min-width: 0 !important;
    max-width: 100%;
    box-sizing: border-box;
  }

  .ielts-passage-questions table .dropdown-selection-field {
    min-width: 0 !important;
    max-width: 100%;
    box-sizing: border-box;
  }
  
  .ielts-passage-questions table:not([data-table-type="tfn"]) th {
    padding: ${LAYOUT.simpleTableHeaderPadding};
    border: ${SIZES.borderWidth} solid ${LAYOUT.simpleTableBorderColor};
    text-align: center;
    vertical-align: middle;
    font-size: ${LAYOUT.simpleTableHeaderFontSize};
    font-weight: ${TYPOGRAPHY.bold};
    line-height: ${LAYOUT.simpleTableHeaderLineHeight};
  }
  
  .ielts-passage-questions table:not([data-table-type="tfn"]) td {
    padding: ${LAYOUT.simpleTableRowPadding};
    height: ${LAYOUT.simpleTableRowMinHeight};
    border: ${SIZES.borderWidth} solid ${LAYOUT.simpleTableBorderColor};
    text-align: left;
    vertical-align: top;
    font-size: ${LAYOUT.simpleTableRowFontSize};
    line-height: ${LAYOUT.simpleTableRowLineHeight};
  }
  
  .ielts-passage-questions table:not([data-table-type="tfn"]) td p {
    margin: ${LAYOUT.simpleTableParagraphMargin};
    line-height: ${LAYOUT.simpleTableRowLineHeight};
  }

  .ielts-passage-questions table:not([data-table-type="tfn"]) td p:last-child {
    margin-bottom: 0;
  }
  
  /* ===================================================================== */
  /* TRUE/FALSE/NOT GIVEN TABLE STYLES */
  /* ===================================================================== */
  
  .ielts-passage-questions table.tiptap-table[data-table-type="tfn"] {
    border-radius: ${SIZES.borderRadiusLg};
    overflow: hidden;
    min-width: 100%;
  }
  
  .ielts-passage-questions table.tiptap-table[data-table-type="tfn"] td {
    padding: ${LAYOUT.tableCellPaddingLarge};
    border: ${SIZES.borderWidth} solid ${COLORS.borderLight};
    text-align: left;
    line-height: ${TYPOGRAPHY.tightLineHeight};
    font-size: ${TYPOGRAPHY.baseFontSize};
  }
  
  .ielts-passage-questions table.tiptap-table[data-table-type="tfn"] td p {
    margin: 0;
    font-size: ${TYPOGRAPHY.baseFontSize};
    line-height: ${TYPOGRAPHY.tightLineHeight};
  }
  
  /* Remove vertical dividers between columns */
  .ielts-passage-questions table.tiptap-table[data-table-type="tfn"] td:first-child {
    border-right: none;
  }
  
  .ielts-passage-questions table.tiptap-table[data-table-type="tfn"] td:last-child {
    border-left: none;
  }
  
  /* Row background colors */
  .ielts-passage-questions table.tiptap-table[data-table-type="tfn"] tr:first-child td {
    background-color: ${COLORS.backgroundLightGray};
    font-weight: ${TYPOGRAPHY.medium};
  }
  
  .ielts-passage-questions table.tiptap-table[data-table-type="tfn"] tr:nth-child(2) td {
    background-color: ${COLORS.white};
  }
  
  .ielts-passage-questions table.tiptap-table[data-table-type="tfn"] tr:last-child td {
    background-color: ${COLORS.backgroundLightGray};
    font-weight: ${TYPOGRAPHY.medium};
  }
  
  /* ===================================================================== */
  /* TYPOGRAPHY */
  /* ===================================================================== */
  
  .ielts-passage-questions h1 {
    font-size: ${TYPOGRAPHY.h1Size};
    margin-bottom: ${TYPOGRAPHY.headingMarginBottom.h1};
    line-height: ${TYPOGRAPHY.headingLineHeight};
    font-weight: ${TYPOGRAPHY.semibold};
    color: ${COLORS.text};
  }
  
  .ielts-passage-questions h2 {
    font-size: ${TYPOGRAPHY.h2Size};
    margin-bottom: ${TYPOGRAPHY.headingMarginBottom.h2};
    line-height: ${TYPOGRAPHY.headingLineHeight};
    font-weight: ${TYPOGRAPHY.semibold};
    color: ${COLORS.text};
  }
  
  .ielts-passage-questions h3 {
    font-size: ${TYPOGRAPHY.h3Size};
    margin-bottom: ${TYPOGRAPHY.headingMarginBottom.h3};
    line-height: ${TYPOGRAPHY.headingLineHeight};
    font-weight: ${TYPOGRAPHY.semibold};
    color: ${COLORS.text};
  }
  
  .ielts-passage-questions p {
    font-size: ${TYPOGRAPHY.baseFontSize};
    margin-bottom: ${TYPOGRAPHY.headingMarginBottom.p};
    color: ${COLORS.textDark};
    line-height: ${TYPOGRAPHY.baseLineHeight};
  }
  
  /* ===================================================================== */
  /* HIDDEN ELEMENTS */
  /* ===================================================================== */
  
  .ielts-passage-questions .answer-wrapper,
  .ielts-passage-questions .correct-answer-indicator {
    display: none !important;
  }
  
  .ielts-passage-questions .question-box-header .question-number,
  .ielts-passage-questions .question-number,
  .ielts-passage-questions .q-marker,
  .ielts-passage-questions .selection-qnum {
    display: none !important;
  }
  
  .ielts-passage-questions .question-number-input {
    display: none !important;
  }
  
  /* ===================================================================== */
  /* QUESTION BOX STYLES */
  /* ===================================================================== */
  
  .ielts-passage-questions .question-box {
    border: none !important;
    border-radius: 0 !important;
    margin-bottom: 0 !important;
    box-shadow: none !important;
    background-color: transparent !important;
    padding: 0 !important;
    width: 100%;
  }
  
  .ielts-passage-questions .question-box-header {
    display: none !important;
  }
  
  .ielts-passage-questions .question-box-content {
    padding: 0 !important;
    background-color: transparent !important;
  }
  
  /* ===================================================================== */
  /* PASSAGE HTML STYLES (Left side in Reading/Writing tests) */
  /* ===================================================================== */
  
  .ielts-passage-html {
    font-size: ${TYPOGRAPHY.baseFontSize};
    line-height: ${TYPOGRAPHY.baseLineHeight};
    color: ${COLORS.textDark};
    max-width: 100%;
    overflow-x: hidden;
  }

  .ielts-passage-html table {
    width: 100% !important;
    max-width: 100% !important;
    table-layout: auto !important;
  }

  .ielts-passage-html table [style*="min-width"] {
    min-width: 0 !important;
  }

  .ielts-passage-html th,
  .ielts-passage-html td {
    overflow-wrap: anywhere;
    word-break: break-word;
    white-space: normal;
    min-width: 0 !important;
  }

  .ielts-passage-html table:not([data-table-type="tfn"]) td {
    line-height: ${LAYOUT.simpleTableRowLineHeight};
  }

  .ielts-passage-html table:not([data-table-type="tfn"]) td p {
    margin: ${LAYOUT.simpleTableParagraphMargin};
    line-height: ${LAYOUT.simpleTableRowLineHeight};
  }

  .ielts-passage-html table:not([data-table-type="tfn"]) td p:last-child {
    margin-bottom: 0;
  }
  
  .ielts-passage-html img {
    max-width: 100%;
    width: 100%;
    height: auto;
    display: block;
    margin: ${SPACING.lg} auto;
    object-fit: contain;
    max-height: 400px;
    border-radius: ${SIZES.borderRadius};
  }
  
  .ielts-passage-html h1 {
    font-size: ${TYPOGRAPHY.h2Size};
    margin-bottom: ${TYPOGRAPHY.headingMarginBottom.h2};
    line-height: ${TYPOGRAPHY.headingLineHeight};
    font-weight: ${TYPOGRAPHY.semibold};
    color: ${COLORS.text};
  }
  
  .ielts-passage-html h2 {
    font-size: ${TYPOGRAPHY.h3Size};
    margin-bottom: ${TYPOGRAPHY.headingMarginBottom.h3};
    line-height: ${TYPOGRAPHY.headingLineHeight};
    font-weight: ${TYPOGRAPHY.semibold};
    color: ${COLORS.text};
  }
  
  .ielts-passage-html h3 {
    font-size: ${TYPOGRAPHY.baseFontSize};
    margin-bottom: ${SPACING.sm};
    line-height: ${TYPOGRAPHY.headingLineHeight};
    font-weight: ${TYPOGRAPHY.semibold};
    color: ${COLORS.text};
  }
  
  /* ===================================================================== */
  /* PASSAGE HTML - TRUE/FALSE/NOT GIVEN TABLE */
  /* ===================================================================== */
  
  .ielts-passage-html .tfn-table,
  .ielts-passage-html table.tiptap-table[data-table-type="tfn"] {
    width: 100%;
    border-collapse: collapse;
    margin: ${LAYOUT.tableMargin};
    border-radius: ${SIZES.borderRadiusLg};
    overflow: hidden;
  }
  
  .ielts-passage-html .tfn-table td,
  .ielts-passage-html table.tiptap-table[data-table-type="tfn"] td {
    padding: ${LAYOUT.tableCellPaddingLarge};
    border: ${SIZES.borderWidth} solid ${COLORS.borderLight};
    text-align: left;
    line-height: ${TYPOGRAPHY.tightLineHeight};
    font-size: ${TYPOGRAPHY.baseFontSize};
  }
  
  .ielts-passage-html .tfn-table td p,
  .ielts-passage-html table.tiptap-table[data-table-type="tfn"] td p {
    margin: 0;
    font-size: ${TYPOGRAPHY.baseFontSize};
    line-height: ${TYPOGRAPHY.tightLineHeight};
  }
  
  /* Remove vertical dividers between columns */
  .ielts-passage-html .tfn-table td:first-child,
  .ielts-passage-html table.tiptap-table[data-table-type="tfn"] td:first-child {
    border-right: none;
  }
  
  .ielts-passage-html .tfn-table td:last-child,
  .ielts-passage-html table.tiptap-table[data-table-type="tfn"] td:last-child {
    border-left: none;
  }
  
  /* Row background colors */
  .ielts-passage-html .tfn-table tr:first-child td,
  .ielts-passage-html table.tiptap-table[data-table-type="tfn"] tr:first-child td {
    background-color: ${COLORS.backgroundLightGray};
    font-weight: ${TYPOGRAPHY.medium};
  }
  
  .ielts-passage-html .tfn-table tr:nth-child(2) td,
  .ielts-passage-html table.tiptap-table[data-table-type="tfn"] tr:nth-child(2) td {
    background-color: ${COLORS.white};
  }
  
  .ielts-passage-html .tfn-table tr:last-child td,
  .ielts-passage-html table.tiptap-table[data-table-type="tfn"] tr:last-child td {
    background-color: ${COLORS.backgroundLightGray};
    font-weight: ${TYPOGRAPHY.medium};
  }
`;

// ============================================================================
// READING-SPECIFIC INLINE STYLES (for narrow split-pane layout)
// ============================================================================
export const ieltsReadingPassageInlineStyles = `
  /* Reading-specific: same as global/admin simple table – preserve column widths from rich text editor */
  .ielts-passage-questions table:not([data-table-type="tfn"]),
  .ielts-passage-questions .tiptap-table:not([data-table-type="tfn"]),
  .ielts-passage-html table:not([data-table-type="tfn"]),
  .ielts-passage-html .tiptap-table:not([data-table-type="tfn"]) {
    width: 100% !important;
    max-width: 100% !important;
    table-layout: fixed !important; /* Preserves colgroup/cell widths set in admin */
  }

  /* Preserve original column widths from admin – only allow shrink on narrow viewports */
  .ielts-passage-questions table:not([data-table-type="tfn"]) colgroup col,
  .ielts-passage-html table:not([data-table-type="tfn"]) colgroup col {
    min-width: 0 !important;
  }

  /* Preserve first-row/cell widths; allow wrapping inside cells */
  .ielts-passage-questions table:not([data-table-type="tfn"]) th,
  .ielts-passage-questions table:not([data-table-type="tfn"]) td,
  .ielts-passage-html table:not([data-table-type="tfn"]) th,
  .ielts-passage-html table:not([data-table-type="tfn"]) td {
    min-width: 0 !important;
    overflow-wrap: anywhere;
    word-break: break-word;
    white-space: normal;
    box-sizing: border-box;
  }
`;
